
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { CloudOff, RefreshCw } from "lucide-react";
import { countPending, onOutboxChange } from "@/lib/local-db";
import { isSyncing, onSyncChange, syncNow } from "@/lib/sync";

function subscribeOnline(cb: () => void) {
  window.addEventListener("online", cb);
  window.addEventListener("offline", cb);
  return () => {
    window.removeEventListener("online", cb);
    window.removeEventListener("offline", cb);
  };
}

export function SyncStatus() {
  // Online status via an external store — no effect setState, SSR-safe.
  const online = useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  );
  const [pending, setPending] = useState(0);
  // The flush itself lives in lib/sync and runs app-wide; this only mirrors it.
  const syncing = useSyncExternalStore(onSyncChange, isSyncing, () => false);

  const refreshCount = useCallback(async () => {
    try {
      setPending(await countPending());
    } catch {
      /* IndexedDB unavailable — ignore */
    }
  }, []);

  // Track the outbox count.
  useEffect(() => {
    // Async IndexedDB read — the setState happens after an await, not a sync cascade.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    refreshCount();
    return onOutboxChange(refreshCount);
  }, [refreshCount]);

  // The count is only re-read on outbox changes, so pick up the flush ending too.
  useEffect(() => {
    if (!syncing) void refreshCount();
  }, [syncing, refreshCount]);

  // Nothing to show when online and fully synced.
  if (online && pending === 0) return null;

  if (!online) {
    return (
      <span className="flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
        <CloudOff className="size-3.5" />
        Offline{pending > 0 ? ` · ${pending}` : ""}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void syncNow()}
      disabled={syncing}
      className="flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200 transition-colors active:bg-blue-200"
    >
      <RefreshCw className={`size-3.5 ${syncing ? "animate-spin" : ""}`} />
      {syncing ? "Syncing…" : `Sync ${pending}`}
    </button>
  );
}
